import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { useTrip } from "./v1/masterData/TripContext";

const BookingContext = createContext();

export const KomisiBookingProvider = ({ children }) => {
  const { trips } = useTrip();

  // 🔹 Memoize `initialSessions` to prevent unnecessary re-renders
  const initialSessions = useMemo(() => {
    return trips.data.map((trip) => ({
      ...trip,
      id: trip.id,
      title: trip.name,
      time: `${trip.start_time} - ${trip.end_time}`,
      stocks: {
        stocks: trip.default_availability,
        booking: 0,
        arrived: 0,
        in_track: 0,
      },
      bg_trip:
        {
          "Trip 1": "bg-yellow-lt",
          "Trip 2": "bg-purple-lt",
          "Trip 3": "bg-blue-lt",
          "Trip 4": "bg-green-lt",
          "Trip 5": "bg-red-lt",
        }[trip.name] || "bg-yellow-lt",
      atvQty: (trip.activities || [])
        .filter((activity) => activity.activity.includes("ATV"))
        .reduce((total, activity) => total + activity.qty, 0),
    }));
  }, [trips.data]);

  // 🔹 Get Date Range for the Last 7 Days
  const getLastWeekRange = () => {
    const today = new Date();
    const lastWeek = new Date();
    // lastWeek.setDate(today.getDate() - 7);
    return {
      gte: lastWeek.toISOString().split("T")[0],
      lte: today.toISOString().split("T")[0],
    };
  };

  const [bookings, setBookings] = useState({
    data: [],
    loading: false,
    pagination: null,
    error: null,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    filters: {
      reservation_date: getLastWeekRange(),
      agent_id: "",
      susuk_agent: "empty",
    },
  });

  const updateFilter = (key, value) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        [key]: value,
      },
    }));
  };

  // 🔹 Handle search when Enter is pressed
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      const value = e.target.value.trim();
      setFilterOptions((prevState) => ({
        ...prevState,
        search: value || "",
      }));
    }
  };

  const [dataPerTrip, setDataPerTrip] = useState({});

  // 🔹 Fetch Bookings with Filters
  const fetchBookings = useCallback(async () => {
    setBookings((prevState) => ({ ...prevState, loading: true }));

    const { gte, lte } = filterOptions.filters.reservation_date;
    const { agent_id, susuk_agent, search, currentPage } = filterOptions;
    const agentIdFilter = filterOptions.filters.agent_id; // Ambil nilai agent_id dari state
    const susukAgent = filterOptions.filters.susuk_agent; // Ambil nilai susuk_agent dari state


    const queryString = buildQueryString({
      search: search || "",
      page: currentPage,
      limit: 200,
      "filter[reservation_date][gte]": gte,
      "filter[reservation_date][lte]": lte,
      "filter[status]": "finished,played",
      // ...(agent_id && { "filter[agent_id]": agent_id }),
      ...(agentIdFilter && { [`filter[agent_id]`]: agentIdFilter }), // Tambahkan jika ada nilai
      // ...(susuk_agent && { "filter[susuk_agent]": susuk_agent }),
      ...(susukAgent && { [`filter[susuk_agent]`]: susukAgent }), // Tambahkan jika ada nilai
      include: "activities,agent,count_logs,labels,guides,customer,trip,productAgent",
    });

    try {
      const response = await getRequest(`/reservation${queryString}`);
      if (response.success) {
        const rawData = response.data;

        // 🔹 Group Bookings by Trip
        const groupedByTrip = rawData.reduce((acc, item) => {
          const tripId = item.trip_id || "Unknown Trip";
          acc[tripId] = acc[tripId] || [];
          acc[tripId].push(item);
          return acc;
        }, {});

        setDataPerTrip(groupedByTrip);
        setBookings({
          data: rawData,
          pagination: response.pagination,
          error: null,
          loading: false,
        });
      }
    } catch (error) {
      setBookings((prevState) => ({
        ...prevState,
        error: "Failed to fetch reservation",
        loading: false,
      }));
      console.error("Error fetching reservations:", error);
    }
  }, [filterOptions]);

  // 🔹 Consolidate `useEffect` Hooks
  useEffect(() => {
    fetchBookings();
  }, [
    filterOptions.filters.reservation_date,
    filterOptions.filters.agent_id,
    filterOptions.filters.susuk_agent,
    filterOptions.search,
  ]);

  return (
    <BookingContext.Provider
      value={{
        sessions: initialSessions,
        isOpenCanvas: useState(false),
        selectedBooking: useState(null),
        bookings,
        filterOptions,
        updateFilter,
        handleSearch,
        dataPerTrip,
        refetch: fetchBookings,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

// 🔹 Hook for Using Booking Context
export const useKomisiBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useKomisiBooking must be used within a KomisiBookingProvider");
  }
  return context;
};
